import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History, X } from "lucide-react";

const KEY = "tr_recent_cases";
const MAX = 8;

/** Read/write helpers — public so other modules (e.g. CaseDetail) can push entries. */
export function recordRecent(c) {
    if (!c || !c.id) return;
    try {
        const raw = localStorage.getItem(KEY);
        let list = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(list)) list = [];
        list = list.filter((x) => x?.id !== c.id);
        list.unshift({
            id: c.id,
            case_number: c.case_number,
            clinic_name: c.clinic_name,
            product: c.product,
            status: c.status,
            at: new Date().toISOString(),
        });
        list = list.slice(0, MAX);
        localStorage.setItem(KEY, JSON.stringify(list));
        window.dispatchEvent(new Event("tr_recent_change"));
    } catch { /* localStorage disabled — ignore */ }
}

function readRecent() {
    try {
        const raw = localStorage.getItem(KEY);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list : [];
    } catch { return []; }
}

/**
 * Salesforce-style "Recently viewed" strip — a horizontal row of recently opened cases.
 * Persists per-browser via localStorage. Auto-refreshes when CaseDetail records a visit.
 */
export default function RecentItems() {
    const [items, setItems] = useState([]);

    useEffect(() => {
        setItems(readRecent());
        const onChange = () => setItems(readRecent());
        window.addEventListener("tr_recent_change", onChange);
        window.addEventListener("storage", onChange);
        return () => {
            window.removeEventListener("tr_recent_change", onChange);
            window.removeEventListener("storage", onChange);
        };
    }, []);

    if (items.length === 0) return null;

    const clear = () => {
        try { localStorage.removeItem(KEY); } catch { /* noop */ }
        setItems([]);
    };

    return (
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white px-4 py-3" data-testid="recent-items">
            <div className="flex items-center justify-between mb-2">
                <div className="inline-flex items-center gap-1.5 font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500">
                    <History size={12} /> Recently viewed
                </div>
                <button onClick={clear} data-testid="recent-clear" className="text-[11px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1">
                    <X size={11} /> Clear
                </button>
            </div>
            <div className="flex flex-wrap gap-2">
                {items.map((it) => (
                    <Link
                        key={it.id}
                        to={`/cases/${it.id}`}
                        data-testid={`recent-item-${it.case_number}`}
                        className="group flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50/60 hover:bg-cyan-50 hover:border-cyan-200 transition"
                    >
                        <span className="font-mono-tr text-[12px] text-slate-900">{it.case_number}</span>
                        <span className="text-[11.5px] text-slate-500 max-w-[160px] truncate">{it.clinic_name}</span>
                    </Link>
                ))}
            </div>
        </div>
    );
}
